import React, { Component } from "react";

import * as d3 from "d3"

import { css } from "@bdfx/mergado-ui-kit";

import { IconArrows, IconRotateLeft } from "app/icons";
import { getCursorByType, getGridMinApproachPx, getHandleSize, getRotateHandleSize, getTemplateSize } from "shape-edit/forms/helpers"

import { GuidesType } from "app/types/app-types";

import { DeltaPositionsType, initDeltaPositions } from "template-edit/containers/image-preview";
import { SizeType } from "app/types/model-types";

export const DRAGGABLE_RECTANGLE_OPACITY = .8
export const DRAGGABLE_ARROW_OPACITY = .5
export const POSITION_INDICATORS_OPACITY = 1

interface Props {
    templateSize: SizeType
    width: number
    height: number
    index: string   //for id references
    x: number       //used to snap on guides
    y: number
    angle: number
    guides?: GuidesType
    isAspectRatio: boolean
    enablePointerEventsOnFrame?: boolean
    showRotateHandle: boolean
    leftHandle?: JSX.Element
    rightHandle?: JSX.Element
    rightIsLockedHandle?: JSX.Element
    showMoveArrows: boolean
    showHandles: boolean
    showFrame: boolean
    deltaAngle: number
    setDeltaAngle: Function
    deltaPositions: DeltaPositionsType
    setDeltaPositions: Function
    handleRotate: Function
    handleResize: Function
    onDragStarted?: Function
    onDragEnded?: Function
    isDragging: boolean
    isLocked?: boolean
}

interface State {
    startX: number
    startY: number
    dragScaleX: number
    dragScaleY: number
    type: ResizeElementType
}

export type ResizeElementType = "top" | "left" | "right" | "bottom" | "rotate" | "move" | "topleft" | "topright" | "bottomleft" | "bottomright"
const allHandleTypes: ResizeElementType[] = ['top', 'topleft', 'left', 'bottomright', 'bottom', 'bottomleft', 'right', 'topright', 'rotate', 'move'];
const MIN_SIZE = 10

const { min, max, abs, sin, cos, PI, round, atan2, ceil, sqrt } = Math

class DraggableRectangle extends Component<Props, State> {
    handleRefs: React.RefObject<SVGRectElement | SVGCircleElement>[] = []

    constructor(props) {
        super(props)

        this.state = {
            startX: 0,
            startY: 0,
            dragScaleX: 1,
            dragScaleY: 1,
            type: null,
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps && !this.props.isDragging && (
            prevProps.index !== this.props.index ||
            prevProps.width !== this.props.width ||
            prevProps.height !== this.props.height ||
            prevProps.angle !== this.props.angle ||
            prevProps.isDragging !== this.props.isDragging ||
            prevProps.isLocked !== this.props.isLocked ||
            prevProps.showHandles !== this.props.showHandles ||
            prevProps.showFrame !== this.props.showFrame ||
            prevProps.showMoveArrows !== this.props.showMoveArrows ||
            prevProps.showRotateHandle !== this.props.showRotateHandle ||
            prevProps.leftHandle !== this.props.leftHandle ||
            prevProps.rightHandle !== this.props.rightHandle ||
            prevProps.rightIsLockedHandle !== this.props.rightIsLockedHandle ||
            prevProps.enablePointerEventsOnFrame !== this.props.enablePointerEventsOnFrame ||
            prevProps.x !== this.props.x ||
            prevProps.y !== this.props.y
        )) {
            this.bindEventsToHandles()
        }
    }

    initDeltaPositionsAndAngle = () => {
        this.props.setDeltaAngle(0)
        this.props.setDeltaPositions({ ...initDeltaPositions })
    }

    getHandleRef = (type: ResizeElementType): React.RefObject<any> => {
        const refString = `handle_${type}`
        if (typeof this.handleRefs[refString] === 'undefined') {
            this.handleRefs[refString] = React.createRef()
        }
        return this.handleRefs[refString]
    }

    bindEventsToHandles = () => {
        allHandleTypes.map(t => {
            const ref = this.getHandleRef(t).current
            if (ref) {
                ref.style.cursor = getCursorByType(t, this.props.angle)
                d3.select(ref)
                    .call(
                        d3.drag()
                            .on("start", (event) => this.dragStarted(event, t))
                            .on("drag", (event) => this.dragged(event, t))
                            .on("end", (event) => this.dragEnded(event, t))
                    )
                    .on("click", () => this.setState({ type: t }))
            }
        })
    }

    componentDidMount() {
        this.bindEventsToHandles()
    }

    dragStarted = (event, type: ResizeElementType) => {
        const { templateSize } = this.props
        this.initDeltaPositionsAndAngle()
        if (typeof this.props.onDragStarted === 'function') {
            this.props.onDragStarted()
        }

        const wrapper = document.getElementById('preview-background-canvas')
        const boundingClientRect = wrapper.getBoundingClientRect()

        const dragScaleX = boundingClientRect?.width ? templateSize.width / boundingClientRect.width : 1
        const dragScaleY = boundingClientRect?.height ? templateSize.height / boundingClientRect.height : 1

        const el = this.getHandleRef(type).current
        if (el) {
            el.style.cursor = getCursorByType(type, this.props.angle || 0)
            el.classList.toggle('active')
            if (event && event.sourceEvent.clientX && event.sourceEvent.clientY) {
                this.setState({
                    startX: event.sourceEvent.clientX,
                    startY: event.sourceEvent.clientY,
                    dragScaleX: dragScaleX,
                    dragScaleY: dragScaleY,
                    type: type,
                })
            }
        }
    }


    dragged = (event, type: ResizeElementType) => {
        const { guides, isAspectRatio, width, height, x, y, angle, index} = this.props

        const el = this.getHandleRef(type).current
        const rotateHandleSize = getRotateHandleSize()

        const alpha = -((angle || 0)) / 180 * PI
        const c = cos(alpha)
        const s = sin(alpha)

        if (el && event && event.sourceEvent.clientY && event.sourceEvent.clientX) {
            const dx_ = round(event.sourceEvent.clientX - this.state.startX)* this.state.dragScaleX
            const dy_ = round(event.sourceEvent.clientY - this.state.startY) * this.state.dragScaleY

            const dx = dx_ * c - dy_ * s
            const dy = dx_ * s + dy_ * c

            const ratio = (height > 0) ? (width / height) : 1

            let deltaPositions: DeltaPositionsType = {
                ...initDeltaPositions,
                index: index,
                type: type,
            }

            switch (type) {
                case 'top':
                    if (isAspectRatio) {
                        deltaPositions.x1 = .5 * dy * ratio
                        deltaPositions.x2 = - .5 * dy * ratio
                    }
                    deltaPositions.y1 = dy
                    break;
                case 'topright':
                    deltaPositions.x2 = dx
                    deltaPositions.y1 = dy
                    break;
                case 'topleft':
                    deltaPositions.x1 = dx
                    deltaPositions.y1 = dy
                    break;
                case 'left':
                    if (isAspectRatio) {
                        deltaPositions.y1 = .5 * dx / ratio
                        deltaPositions.y2 = - .5 * dx / ratio
                    }
                    deltaPositions.x1 = dx
                    break;
                case 'right':
                    if (isAspectRatio) {
                        deltaPositions.y1 = - .5 * dx / ratio
                        deltaPositions.y2 = .5 * dx / ratio
                    }
                    deltaPositions.x2 = dx
                    break;
                case 'bottom':
                    if (isAspectRatio) {
                        deltaPositions.x1 = - .5 * dy * ratio
                        deltaPositions.x2 = .5 * dy * ratio
                    }
                    deltaPositions.y2 = dy
                    break;
                case 'bottomleft':
                    deltaPositions.x1 = dx
                    deltaPositions.y2 = dy
                    break;
                case 'bottomright':
                    deltaPositions.x2 = dx
                    deltaPositions.y2 = dy
                    break;
                case 'move':
                    deltaPositions.x1 = dx
                    deltaPositions.x2 = dx
                    deltaPositions.y1 = dy
                    deltaPositions.y2 = dy
                    break;
                case 'rotate':
                    const rotateHandleOffset = this.getRotateHandleOffset(rotateHandleSize)

                    this.setState({ type: type })

                    const alpha = atan2(event.y + rotateHandleOffset.dy, event.x + rotateHandleOffset.dx) * 180 / PI + 90
                    /* const alpha = atan2(
                        (event.sourceEvent.clientY - this.state.startY + rotateHandleOffset.dy) * this.state.dragScaleY,
                        (event.sourceEvent.clientX - this.state.startX + rotateHandleOffset.dx) * this.state.dragScaleX
                    ) * 180 / PI + 90 */
                    /* const alpha = atan2(
                        (event.sourceEvent.clientY - this.state.startY + rotateHandleOffset.dy) * this.state.dragScaleY,
                        (event.sourceEvent.clientX - this.state.startX + rotateHandleOffset.dx) * this.state.dragScaleX
                    ) * 180 / PI + 90 */
                    this.setState({ type: type })
                    this.props.setDeltaAngle((this.props.deltaAngle + alpha) % 360)
                    break;
            }

            if (guides && type !== 'rotate') {
                    const realPos = {
                        x1: x - width / 2 + deltaPositions.x1,
                        x2: x + width / 2 + deltaPositions.x2,
                        y1: y - height / 2 + deltaPositions.y1,
                        y2: y + height / 2 + deltaPositions.y2,
                    }

                    const nearestGridPointsDiff = {
                        x1: round(realPos.x1 / guides.gridStep) * guides.gridStep - realPos.x1,
                        y1: round(realPos.y1 / guides.gridStep) * guides.gridStep - realPos.y1,
                        x2: round(realPos.x2 / guides.gridStep) * guides.gridStep - realPos.x2,
                        y2: round(realPos.y2 / guides.gridStep) * guides.gridStep - realPos.y2,
                    }

                    const minApproachPx = getGridMinApproachPx(guides.gridStep)

                    if (type.includes('top') && abs(nearestGridPointsDiff.y1) < minApproachPx) {
                        const dy = nearestGridPointsDiff.y1
                        deltaPositions.y1 += dy
                        if (isAspectRatio) {
                            deltaPositions.x1 += .5 * dy * ratio
                            deltaPositions.x2 += -.5 * dy * ratio
                        }
                    } else if (type.includes('bottom') && abs(nearestGridPointsDiff.y2) < minApproachPx) {
                        const dy = nearestGridPointsDiff.y2
                        deltaPositions.y2 += dy
                        if (isAspectRatio) {
                            deltaPositions.x1 += .5 * dy * ratio
                            deltaPositions.x2 += -.5 * dy * ratio
                        }
                    }

                    if (type.includes('left') && abs(nearestGridPointsDiff.x1) < minApproachPx) {
                        const dx = nearestGridPointsDiff.x1
                        deltaPositions.x1 += dx
                        if (isAspectRatio) {
                            deltaPositions.y1 += .5 * dx * ratio
                            deltaPositions.y2 += -.5 * dx * ratio
                        }
                    } else if (type.includes('right') && abs(nearestGridPointsDiff.x2) < minApproachPx) {
                        const dx = nearestGridPointsDiff.x2
                        deltaPositions.x2 += dx
                        if (isAspectRatio) {
                            deltaPositions.y1 += .5 * dx * ratio
                            deltaPositions.y2 += -.5 * dx * ratio
                        }
                    }
            }

            const deltaWidth = deltaPositions.x2 - deltaPositions.x1
            const deltaHeight = deltaPositions.y2 - deltaPositions.y1

            const newWidth = width + deltaWidth
            const newHeight = height + deltaHeight

            if (newWidth > MIN_SIZE && newHeight > MIN_SIZE) {
                this.props.setDeltaPositions({ ...deltaPositions, index, type })
            }
        }
    }

    dragEnded = (event, type: ResizeElementType) => {
        const {angle} = this.props
        const el = this.getHandleRef(type).current
        if (el) {
            el.style.cursor = getCursorByType(type, angle)
            el.classList.toggle('active')

            if (type === 'rotate') {
                this.props.handleRotate()
            } else {
                this.props.handleResize(type)
            }

            this.setState({type: null})
        }

        if (typeof this.props.onDragEnded === 'function') {
            this.props.onDragEnded()
        }
    }

    round = (number: number, increment: number) => {
        return Math.ceil((number) / increment) * increment;
    }

    getRotateHandleOffset = (rotateHandleSize: number): { dx: number, dy: number } => {
        return {
            dx: -.5 * rotateHandleSize,
            dy: -.5 * rotateHandleSize - 1.2 * rotateHandleSize,
        }
    }

    render() {
        const { showHandles, angle, deltaPositions, width, height, index, enablePointerEventsOnFrame,
            showRotateHandle, leftHandle, rightHandle, rightIsLockedHandle, showMoveArrows, showFrame,
            isDragging, isLocked } = this.props

        const {type} = this.state

        if (!width || !height) {
            return false
        }

        const isThisDragged = deltaPositions.index === index

        let x1 = -.5 * width,
            x2 = .5 * width,
            y1 = -.5 * height,
            y2 = .5 * height,
            deltaWidth = 0,
            deltaHeight = 0,
            deltaX = 0,
            deltaY = 0

        if (isThisDragged) {
            x1 += deltaPositions.x1
            x2 += deltaPositions.x2
            y1 += deltaPositions.y1
            y2 += deltaPositions.y2

            deltaWidth = deltaPositions.x2 - deltaPositions.x1
            deltaHeight = deltaPositions.y2 - deltaPositions.y1

            deltaX = deltaPositions.x1 + .5 * deltaWidth
            deltaY = deltaPositions.y1 + .5 * deltaHeight
        }

        const newWidth = abs(width + deltaWidth)
        const newHeight = abs(height + deltaHeight)

        const rotateHandleSize = getRotateHandleSize()
        const handleSize = getHandleSize()
        const handleSizeLong = handleSize * 4
        const handleSizeShort = max(handleSize / 3, 3)
        const outlineSize = max(handleSize / 10, 1)

        const rotateHandleOffset = this.getRotateHandleOffset(rotateHandleSize)
        const handleClass = isDragging || !showHandles ? 'hidden' : ''

        const tagWidth = rotateHandleSize * 8
        const tagHeight = rotateHandleSize * 2
        const tagWidthRotate = rotateHandleSize * 3

        const tagFontSize = rotateHandleSize * 1.2
        const rounding = rotateHandleSize / 4

        //offsetX, offsetY  is for rotation
        let offsetX = 0
        let offsetY = 0

        switch (type) {
            case 'topleft':
                offsetX = 0;
                offsetY = rotateHandleOffset.dy;
                break;
            case 'topright':
                offsetX = newWidth;
                offsetY = rotateHandleOffset.dy;
                break;
            case 'bottomleft':
                offsetX = 0;
                offsetY = newHeight - rotateHandleOffset.dy;
                break;
            case 'bottomright':
                offsetX = newWidth;
                offsetY = newHeight - rotateHandleOffset.dy;
                break;
            case 'left':
                offsetX = -tagWidth / 2;
                offsetY = newHeight / 2;
                break;
            case 'right':
                offsetX = newWidth + tagWidth / 2;
                offsetY = newHeight / 2;
                break;
            case 'bottom':
                offsetX = newWidth/2 + .5*rotateHandleSize;
                offsetY = newHeight - rotateHandleOffset.dy;
                break;
            case 'top':
                offsetX = newWidth / 2 + .5*rotateHandleSize;
                offsetY = rotateHandleOffset.dy;
                break;
        }

        const transform1 = `translate(${x1 + .5 * newWidth - tagWidthRotate / 2}, ${y1 - tagHeight / 2 + rotateHandleOffset.dy}) rotate(${-angle - this.props.deltaAngle}, ${tagWidthRotate / 2}, ${tagHeight / 2})`
        const transform2 = `translate(${x1 + offsetX - tagWidth/2}, ${y1 + offsetY - tagHeight/2}) rotate(${-angle - this.props.deltaAngle}, ${tagWidth/2}, ${tagHeight/2})`
        const transform3 = `translate(${x1 + .5 * newWidth - tagWidth / 2}, ${y1 + newHeight + tagHeight / 2}) rotate(${-angle - this.props.deltaAngle}, ${tagWidth / 2}, ${tagHeight / 2})`

        return (
            <StyledDecorator handleSize={handleSize} handleSizeLong={handleSizeLong} handleSizeShort={handleSizeShort} outlineSize={outlineSize} style={{ pointerEvents: 'auto' }}>
                <g className={`draggable-rectangle ${showHandles ? 'show-handles' : ''}`} style={{opacity: showFrame ? 1 : undefined}}>
                    <g className="draggable-arrow" id={`middle-shape-${index}`} ref={this.getHandleRef('move')}>
                        {showMoveArrows &&
                            <g transform={`translate(${-.5 * rotateHandleSize * 2},${-.5 * rotateHandleSize * 2})`} width={rotateHandleSize * 2} height={rotateHandleSize * 2} className={handleClass}>
                                <IconArrows size={rotateHandleSize * 2} />
                            </g>
                        }
                    </g>

                        <rect
                            className={'decorator-frame'}
                            width={newWidth}
                            height={newHeight}
                            fill='transparent'
                            transform={`translate(${-.5 * newWidth + deltaX}, ${-.5 * newHeight + deltaY})`}
                            style={{
                                pointerEvents: enablePointerEventsOnFrame ? 'none' : 'stroke',
                                outline: `3px dashed #ccc`,
                            }}
                        />

                    {showRotateHandle && !isLocked &&
                        <React.Fragment>
                            <g ref={this.getHandleRef('rotate')} className={`rotate-handle ${handleClass}`}
                                transform={`translate(${x1 + rotateHandleOffset.dx + .5 * newWidth}, ${y1 + rotateHandleOffset.dy})`}
                                width={rotateHandleSize}
                                height={rotateHandleSize}>
                                <g>
                                    <rect
                                        style={{ fill: 'transparent', stroke: 'transparent' }}
                                        width={rotateHandleSize}
                                        height={rotateHandleSize}
                                    />
                                    <IconRotateLeft size={rotateHandleSize} />
                                </g>
                            </g>
                        </React.Fragment>
                    }

                    {leftHandle && !isLocked &&
                        <g className={`left-handle ${handleClass}`}
                            transform={`translate(${x1 + rotateHandleOffset.dx + .5 * newWidth - 1.2 * rotateHandleSize}, ${y1 + rotateHandleOffset.dy})`}>
                            {leftHandle}
                        </g>
                    }


                    {rightHandle && !isLocked &&
                        <g className={`right-handle ${handleClass}`}
                            transform={`translate(${x1 + rotateHandleOffset.dx + .5 * newWidth + 1.2 * rotateHandleSize}, ${y1 + rotateHandleOffset.dy})`}>
                            {rightHandle}
                        </g>
                    }

                    {isLocked && rightIsLockedHandle &&
                        <g className={`right-handle`}
                            transform={`translate(${x1 + rotateHandleOffset.dx + .5 * newWidth + 1.2 * rotateHandleSize}, ${y1 + rotateHandleOffset.dy})`}>
                            {rightIsLockedHandle}
                        </g>
                    }

                    {!isLocked &&
                        <React.Fragment>
                            <rect ref={this.getHandleRef('topleft')} width={handleSize} height={handleSize} className={`resizable-handle topleft ${handleClass}`} x={x1 - .5 * handleSize} y={y1 - .5 * handleSize} />
                            <rect ref={this.getHandleRef('topright')} width={handleSize} height={handleSize} className={`resizable-handle topright ${handleClass}`} x={x2 - .5 * handleSize} y={y1 - .5 * handleSize} />
                            <rect ref={this.getHandleRef('bottomleft')} width={handleSize} height={handleSize} className={`resizable-handle bottomleft ${handleClass}`} x={x1 - .5 * handleSize} y={y2 - .5 * handleSize} />
                            <rect ref={this.getHandleRef('bottomright')} width={handleSize} height={handleSize} className={`resizable-handle bottomright ${handleClass}`} x={x2 - .5 * handleSize} y={y2 - .5 * handleSize} />

                            <rect ref={this.getHandleRef('left')} width={handleSizeShort} height={handleSizeLong} className={`resizable-handle left ${handleClass}`} x={x1 - handleSizeShort} y={y1 - .5 * handleSizeLong + .5 * newHeight} />
                            <rect ref={this.getHandleRef('right')} width={handleSizeShort} height={handleSizeLong} className={`resizable-handle right ${handleClass}`} x={x2} y={y1 - .5 * handleSizeLong + .5 * newHeight} />
                            <rect ref={this.getHandleRef('top')} width={handleSizeLong} height={handleSizeShort} className={`resizable-handle top ${handleClass}`} x={x1 - .5 * handleSizeLong + .5 * newWidth} y={y1 - handleSizeShort} />
                            <rect ref={this.getHandleRef('bottom')} width={handleSizeLong} height={handleSizeShort} className={`resizable-handle bottom ${handleClass}`} x={x1 - .5 * handleSizeLong + .5 * newWidth} y={y2} />
                        </React.Fragment>
                    }
                </g>

                {showHandles && (this.props.deltaPositions.x1 || this.props.deltaPositions.x2 || this.props.deltaPositions.y1 || this.props.deltaPositions.y2 || this.props.deltaAngle) &&
                    <g className="position-indicators">
                        {this.props.deltaAngle && this.state.type === 'rotate' &&
                            <g transform={transform1}>
                                <rect
                                    width={tagWidthRotate}
                                    height={tagHeight}
                                    rx={rounding}
                                    ry={rounding}
                                />
                                <text
                                    x={tagWidthRotate/2}
                                    y={tagHeight/2}
                                    dominantBaseline={"central"}
                                    textAnchor={"middle"}
                                    fontSize={tagFontSize}>{round((angle + this.props.deltaAngle + 360)) % 360}°</text>
                            </g>
                        }

                        {this.state.type && !['rotate','move'].includes(this.state.type) && (this.props.deltaPositions.x1 || this.props.deltaPositions.x2 || this.props.deltaPositions.y1 || this.props.deltaPositions.y2) &&
                            <g transform={transform2}>
                                <rect
                                    width={tagWidth}
                                    height={tagHeight}
                                    rx={rounding}
                                    ry={rounding}
                                />
                                <text
                                    x={tagWidth / 2}
                                    y={tagHeight / 2}
                                    dominantBaseline={"central"}
                                    textAnchor={"middle"}
                                    fontSize={tagFontSize}>w:{round(newWidth)} h:{round(newHeight)}</text>
                            </g>
                        }

                        {(!this.state.type || this.state.type === 'move') && (this.props.deltaPositions.x1 || this.props.deltaPositions.x2 || this.props.deltaPositions.y1 || this.props.deltaPositions.y2) &&
                            <g transform={transform3}>
                                <rect
                                    width={tagWidth}
                                    height={tagHeight}
                                    rx={rounding}
                                    ry={rounding}
                                />
                                <text
                                    x={tagWidth/2}
                                    y={tagHeight/2}
                                    dominantBaseline={"central"}
                                    textAnchor={"middle"}
                                    fontSize={tagFontSize}>x:{round(this.props.x + deltaX)} y:{round(this.props.y + deltaY)}</text>
                            </g>
                        }
                    </g>
                }
            </StyledDecorator>
        )
    }
}

const StyledDecorator = css('g')({
    " .draggable-rectangle": {
        opacity: 0,
        position: "absolute",
    },
    " .show-handles": {
        opacity: DRAGGABLE_RECTANGLE_OPACITY,
    },
    " .hidden": {
        display: 'none',
        visibility: 'hidden',
    },
    " .position-indicators": {
        opacity: POSITION_INDICATORS_OPACITY,
        " text": {
            fontSmooth: 'never',
            fontWeight: 'bold',
            stroke: '#000000',
            strokeWidth: 0,
            fill: '#6b8ab0',
        },
        " rect": {
            stroke: 'transparent',
            strokeWidth: 0,
            fill: '#3a3a3a',
        }
    },
}, props => ({
    " .selected-shape .decorator-frame": {
        outline: `${props.outlineSize}px dashed #333`,
        mixBlendMode: 'difference',
    },
    " .resizable-handle": {
        mixBlendMode: 'color',
        outline: `${props.outlineSize}px solid #3a3a3a`,
        fill: 'rgba(250, 250, 250, 0.8)',
        width: props.handleSize,
        height: props.handleSize,
    },
    " .left, .right": {
        opacity: 1,
        mixBlendMode: 'color',
        outline: `${props.outlineSize}px solid #3a3a3a`,
        fill: '#fafafa',
        width: props.handleSizeShort,
        height: props.handleSizeLong,
    },
    " .top, .bottom": {
        opacity: 1,
        mixBlendMode: 'color',
        outline: `${props.outlineSize}px solid #3a3a3a`,
        fill: '#fafafa',
        width: props.handleSizeLong,
        height: props.handleSizeShort,
    },
}))

export default DraggableRectangle
